import { ReactNode, useEffect } from 'react';
import { Box } from '@mui/material';
import { AuthBrandPanel } from './auth-brand-panel';
import { AuthBranding } from './auth-branding';

// Single responsive breakpoint from the handoff (column -> row at 940px).
const DESKTOP = '@media (min-width:940px)';

interface AuthLayoutProps {
  branding: AuthBranding;
  /** Show the Gaia marketing headline/eyebrow on the brand panel (desktop). */
  showEditorial?: boolean;
  /** Max width of the form content column. */
  formMaxWidth?: number;
  children: ReactNode;
}

/**
 * Shared split-screen shell for every auth/onboarding screen: full-bleed brand
 * panel (photo + wash + logo + trust) on the left, a vertically centred form
 * column on the right. Pages slot their form/content as children. See
 * design_handoff_auth_redesign/README.md.
 */
export const AuthLayout = ({
  branding,
  showEditorial = branding.hasEditorialCopy,
  formMaxWidth = 400,
  children,
}: AuthLayoutProps) => {
  // Reserve the scrollbar gutter on the document (the actual scroll container)
  // while an auth screen is mounted. The columns are sized in viewport-relative
  // units, so without this the vertical scrollbar appearing or disappearing
  // (e.g. as a validation message shows) changes the viewport width and nudges
  // both columns. Scoped to auth: restored on unmount so no other page shifts.
  useEffect(() => {
    const html = document.documentElement;
    const previous = html.style.getPropertyValue('scrollbar-gutter');
    html.style.setProperty('scrollbar-gutter', 'stable');
    return () => {
      if (previous) html.style.setProperty('scrollbar-gutter', previous);
      else html.style.removeProperty('scrollbar-gutter');
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: '100vh',
        backgroundColor: 'common.white',
        color: 'text.primary',
        boxSizing: 'border-box',
        [DESKTOP]: { flexDirection: 'row' },
      }}
    >
      <AuthBrandPanel branding={branding} showEditorial={showEditorial} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          p: '40px 24px 48px',
          [DESKTOP]: { flex: '0 0 47%', maxWidth: 620, minHeight: '100vh', p: '56px 64px' },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: formMaxWidth, minWidth: 0 }}>{children}</Box>
      </Box>
    </Box>
  );
};
